import {
  canvasOf, components, enhance, gradient, grayscale, pixelsOf, warp,
} from './imaging';

// Turning a photograph of a piece of paper into something that reads like a scan: find the sheet on
// the counter, look at it straight on, and lift the ink off the page.
//
// This matters twice over. The clerk gets a document they can actually read back weeks later rather
// than a snapshot taken at an angle under a tube light, and the recogniser gets the flat, high
// contrast image those models were trained on — a challan photographed at thirty degrees reads far
// worse than the same challan squared up.

// Detection runs on a reduced copy: the edge of a sheet of paper is a large feature, and looking for
// it at full resolution costs time and finds the texture of the paper instead.
const DETECT_EDGE = 900;
// The rectified page. Larger than the recogniser needs, because this image is also the record kept
// on the trip, and someone may want to read a hand-written quantity off it later.
const MAX_OUTPUT = 2600;
// A quadrilateral smaller than this much of the frame is something else lying on the desk. One
// larger than the upper bound is the frame itself — a page already flat and filling the picture,
// which is the ordinary case for an uploaded scan and needs no correcting.
const MIN_AREA = 0.2;
const MAX_AREA = 0.985;
// The share of the picture treated as edge. Paper against a desk is a strong edge; this keeps the
// strongest few per cent and lets the rest go.
const EDGE_SHARE = 0.06;

// The smallest convex outline containing every edge point — Andrew's monotone chain. The page's
// corners lie on this hull whatever clutter sits inside it, which is what the pass buys.
const hullOf = (points) => {
  const sorted = [...points].sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
  const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const half = (list) => {
    const out = [];
    list.forEach((point) => {
      while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], point) <= 0) out.pop();
      out.push(point);
    });
    return out;
  };
  return [...half(sorted).slice(0, -1), ...half([...sorted].reverse()).slice(0, -1)];
};

// Corners in a known order — top left, top right, bottom right, bottom left — so the warp does not
// mirror or rotate the page. The sum and difference of each point's coordinates separate them
// without any trigonometry: the top left has the smallest sum, the bottom right the largest.
const cornersOf = (hull) => {
  const pick = (score, highest) => hull.reduce((best, point) => {
    if (highest) return score(point) > score(best) ? point : best;
    return score(point) < score(best) ? point : best;
  }, hull[0]);
  const sum = (p) => p.x + p.y;
  const diff = (p) => p.y - p.x;
  return [pick(sum, false), pick(diff, false), pick(sum, true), pick(diff, true)];
};

const areaOf = ([a, b, c, d]) => Math.abs(
  (a.x * b.y - b.x * a.y) + (b.x * c.y - c.x * b.y) + (c.x * d.y - d.x * c.y) + (d.x * a.y - a.x * d.y),
) / 2;

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

// The sheet, as four corners in the coordinates of the image passed in, or null when nothing in the
// frame looks like a page held at an angle — a challan against a busy desk, or one already flat and
// filling the picture. Both are ordinary, and both are handled by scanning what was photographed.
const findPage = (canvas) => {
  const scale = Math.min(1, DETECT_EDGE / Math.max(canvas.width, canvas.height));
  const small = canvasOf(canvas, canvas.width * scale, canvas.height * scale);
  const edges = gradient(grayscale(pixelsOf(small)));

  // The threshold comes from the picture rather than being fixed: a challan on a dark desk and one
  // on a white counter have nothing in common except that their edges are the strongest thing in
  // the frame.
  const histogram = new Uint32Array(256);
  for (let i = 0; i < edges.data.length; i += 1) histogram[edges.data[i]] += 1;
  let keep = edges.data.length * EDGE_SHARE;
  let threshold = 255;
  for (let value = 255; value > 0 && keep > 0; value -= 1) { keep -= histogram[value]; threshold = value; }

  const mask = new Uint8Array(edges.data.length);
  for (let i = 0; i < edges.data.length; i += 1) mask[i] = edges.data[i] >= threshold ? 1 : 0;

  // The page's own border is the largest thing those edges form. Taking the biggest blob first
  // discards the lettering, the desk grain and whatever else is lying beside the challan.
  const blobs = components(mask, edges.width, edges.height);
  if (!blobs.length) return null;
  const biggest = blobs.reduce((best, blob) => {
    const area = (blob.right - blob.left) * (blob.bottom - blob.top);
    return area > (best.right - best.left) * (best.bottom - best.top) ? blob : best;
  });
  const points = [];
  for (let y = biggest.top; y <= biggest.bottom; y += 1) {
    for (let x = biggest.left; x <= biggest.right; x += 1) {
      if (mask[y * edges.width + x]) points.push({ x, y });
    }
  }
  if (points.length < 16) return null;

  const corners = cornersOf(hullOf(points));
  const frame = edges.width * edges.height;
  const area = areaOf(corners);
  if (area < frame * MIN_AREA || area > frame * MAX_AREA) return null;
  // Four corners collapsed onto nearly the same point are not a page: every side needs some length.
  const sides = [
    distance(corners[0], corners[1]), distance(corners[1], corners[2]),
    distance(corners[2], corners[3]), distance(corners[3], corners[0]),
  ];
  if (Math.min(...sides) < Math.max(edges.width, edges.height) * 0.15) return null;
  return corners.map((point) => ({ x: point.x / scale, y: point.y / scale }));
};

/**
 * A photograph in, a scan out.
 *
 * Returns the rectified, enhanced page as a canvas, and whether a page was actually found — the
 * caller says which on screen rather than quietly implying the correction happened.
 */
const scanDocument = async (source) => {
  const width = source.videoWidth || source.naturalWidth || source.width;
  const height = source.videoHeight || source.naturalHeight || source.height;
  const shrink = Math.min(1, MAX_OUTPUT / Math.max(width, height));
  const input = canvasOf(source, width * shrink, height * shrink);
  const corners = findPage(input);
  const pixels = pixelsOf(input);

  let flattened = pixels;
  if (corners) {
    // The rectified size comes from the page's own edges, so a challan photographed from its left
    // keeps its proportions instead of being stretched into the shape of the frame.
    const [tl, tr, br, bl] = corners;
    const across = Math.round(Math.max(distance(tl, tr), distance(bl, br)));
    const down = Math.round(Math.max(distance(tl, bl), distance(tr, br)));
    flattened = warp(pixels, corners, across, down) || pixels;
  }
  const finished = enhance(flattened);
  const canvas = document.createElement('canvas');
  canvas.width = finished.width; canvas.height = finished.height;
  canvas.getContext('2d').putImageData(finished, 0, 0);
  return { canvas, corrected: !!corners };
};

export default scanDocument;
